import type { BondOrder } from './editor';
import type { DrawingDocument } from '../model';
import { bondStereoValues, drawnBondOrders } from './documentSelectors.ts';
import { changeDepiction, changeStructure, roundCoordinate } from './drawingDocumentCore.ts';
import { documentToAtoms, documentToLines } from './drawingDocumentProjections.ts';
import { fusedRingDoubleBondSides } from './objectEditing.ts';
import { reconcileValenceCharges } from './valenceCharge.ts';
import { deleteBond } from './graphTopologyMutations.ts';
import { IONIC_ELECTRONEGATIVITY_DIFFERENCE, paulingElectronegativity } from '../chemistry/electronegativity.ts';

export function setAtomAtomicNumber(document: DrawingDocument, nodeId: string, atomicNumber: number): DrawingDocument {
  const current = document.structure.atoms[nodeId];
  if (
    !current ||
    current.atomicNumber === atomicNumber ||
    !Number.isInteger(atomicNumber) ||
    atomicNumber < 1 ||
    atomicNumber > 118
  ) {
    return document;
  }
  // A specific isotope mass number or radical-electron count belongs to the atom's previous
  // element; changing the element invalidates both. It also invalidates this atom's own
  // stereocentre and incident chemical stereo assignments. Preserve authored wedges (#1071).
  // Stereo elsewhere in the drawing stays intact (#372).
  // Rule 4.4.1: reconcile so a retyped over-valent N / O picks up (or drops) its auto charge.
  return reconcileValenceCharges(
    changeStructure(
      document,
      {
        atoms: { ...document.structure.atoms, [nodeId]: { atomicNumber } }
      },
      { clearAromaticity: true, clearStereoForNode: nodeId, preserveWedgeStyles: true }
    ),
    document
  );
}

export function setAtomIsotope(document: DrawingDocument, nodeId: string, isotope: number | null): DrawingDocument {
  const current = document.structure.atoms[nodeId];
  if (
    !current ||
    (current.isotope ?? null) === isotope ||
    (isotope !== null && (!Number.isInteger(isotope) || isotope < 1 || isotope > 300))
  ) {
    return document;
  }
  const { isotope: _existingIsotope, ...withoutIsotope } = current;
  return changeStructure(
    document,
    {
      atoms: {
        ...document.structure.atoms,
        [nodeId]: isotope === null ? withoutIsotope : { ...withoutIsotope, isotope }
      }
    },
    { clearAromaticity: true, clearStereoForNode: nodeId }
  );
}

export function setAtomRadicalElectronCount(
  document: DrawingDocument,
  nodeId: string,
  radicalElectronCount: number | null
): DrawingDocument {
  const current = document.structure.atoms[nodeId];
  if (
    !current ||
    (current.radicalElectronCount ?? null) === radicalElectronCount ||
    (radicalElectronCount !== null &&
      (!Number.isInteger(radicalElectronCount) || radicalElectronCount < 1 || radicalElectronCount > 3))
  ) {
    return document;
  }
  const { radicalElectronCount: _existingRadicalElectronCount, ...withoutRadical } = current;
  return changeStructure(
    document,
    {
      atoms: {
        ...document.structure.atoms,
        [nodeId]: radicalElectronCount === null ? withoutRadical : { ...withoutRadical, radicalElectronCount }
      }
    },
    { clearAromaticity: true, clearStereoForNode: nodeId }
  );
}

export function setBondOrder(document: DrawingDocument, edgeId: string, order: BondOrder): DrawingDocument {
  const current = document.structure.bonds[edgeId];
  if (!current) return document;
  // ADR 0022 D6: the Single / Double / Triple tool on a bond already of that order clears its
  // marking (wedge, wavy, crossed, dative) -- the Bonds group has no "clear" button any more.
  if (current.order === order) return clearBondMarking(document, edgeId);
  // Raising a dative bond's order past 1 makes it an ordinary covalent multiple bond; a hydrogen
  // bond (order 0) given an order becomes a covalent bond.
  const nextBond = current.type === 'covalent' ? { ...current, order } : { order, type: 'covalent' as const };
  // Only *this* bond's own stereo is invalidated by its order change (a wedge needs order 1,
  // a crossed-double / cis-trans config needs order 2). A crossed double bond or wedge on any
  // other bond in the drawing is left untouched -- observation from manual testing (#372).
  // Rule 4.4.1: changing an order changes both endpoints' bond sums -- reconcile N / O charges.
  return reconcileValenceCharges(
    changeStructure(
      document,
      {
        bonds: { ...document.structure.bonds, [edgeId]: nextBond }
      },
      { clearAromaticity: true, clearStereoForEdge: edgeId }
    ),
    document
  );
}

/** ADR 0022 D6: a bond's wedge, wavy, crossed or dative marking removed; the bond's order stays. */
export function clearBondMarking(document: DrawingDocument, edgeId: string): DrawingDocument {
  const stereo = bondStereoValues(document)[edgeId];
  if (stereo?.type === 'dative') return setBondDative(document, edgeId, null);
  if (stereo?.type === 'crossed-double') return setBondCrossedDouble(document, edgeId, false);
  if (stereo?.type === 'wedge' || stereo?.type === 'wavy') {
    const { [edgeId]: _cleared, ...bondStyles } = document.depiction.bondStyles;
    return changeDepiction(document, { bondStyles });
  }
  return document;
}

// ADR 0022 D3: which of two atoms is the cation -- the less electronegative; `source` when the
// table doesn't know one of them (the bolt is drawn from the cation).
function cationOf(document: DrawingDocument, source: string, target: string): string {
  const of = (id: string) => paulingElectronegativity(document.structure.atoms[id]?.atomicNumber ?? 0);
  const [first, second] = [of(source), of(target)];
  return first !== undefined && second !== undefined && second < first ? target : source;
}

/**
 * ADR 0022 (as amended 2026-10-27): make two atoms a salt's ions -- the cation (the less
 * electronegative) one charge more, the anion one less, unless they are already oppositely
 * charged. No bond is stored: a salt is its separate ions (IUPAC), and the paper draws the bolt
 * between them on its own (`ionicPairs`). Ca with two Cl, ionized twice, becomes Ca2+.
 */
export function ionizePair(document: DrawingDocument, first: string, second: string): DrawingDocument {
  if (first === second || !document.structure.atoms[first] || !document.structure.atoms[second]) return document;
  const charges = document.structure.formalCharges;
  if ((charges[first] ?? 0) * (charges[second] ?? 0) < 0) return document;
  const cation = cationOf(document, first, second);
  const anion = cation === first ? second : first;
  return changeStructure(
    document,
    {
      formalCharges: { ...charges, [cation]: (charges[cation] ?? 0) + 1, [anion]: (charges[anion] ?? 0) - 1 }
    },
    { clearAromaticity: true }
  );
}

/**
 * ADR 0022 D3 / ADR 0020 fix C: split a single bond between atoms that are oppositely charged or
 * differ in electronegativity by at least IONIC_ELECTRONEGATIVITY_DIFFERENCE (Na–O, Na–Cl) into
 * its two ions. Any other bond is left alone.
 */
export function ionizeBond(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const bond = document.structure.bonds[edgeId];
  if (!edge || bond?.order !== 1 || bond.type === 'hydrogen') return document;
  const charges = document.structure.formalCharges;
  const opposite = (charges[edge.source] ?? 0) * (charges[edge.target] ?? 0) < 0;
  const [a, b] = [edge.source, edge.target].map((id) =>
    paulingElectronegativity(document.structure.atoms[id]?.atomicNumber ?? 0)
  );
  const polar = a !== undefined && b !== undefined && Math.abs(a - b) >= IONIC_ELECTRONEGATIVITY_DIFFERENCE;
  if (!opposite && !polar) return document;
  return splitBondIntoIons(document, edgeId);
}

/** ADR 0020 fix C (#1101): the bond removed and its two atoms made ions -- once the check said so. */
export function splitBondIntoIons(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  return edge ? ionizePair(deleteBond(document, edgeId), edge.source, edge.target) : document;
}

/** ADR 0022 D4: make a bond a hydrogen bond -- dashed, no valence, never part of a molecule. */
export function setBondHydrogen(document: DrawingDocument, edgeId: string): DrawingDocument {
  const current = document.structure.bonds[edgeId];
  if (!current || current.type === 'hydrogen') return document;
  return reconcileValenceCharges(
    changeStructure(
      document,
      { bonds: { ...document.structure.bonds, [edgeId]: { order: 0, type: 'hydrogen' } } },
      { clearAromaticity: true, clearStereoForEdge: edgeId }
    ),
    document
  );
}

/**
 * ADR 0026 D6: make a bond an agostic interaction -- a C–H bond's hydrogen donating into a metal:
 * order 0, no valence, never part of a molecule, drawn from the C–H bond to the metal. One end must
 * be a hydrogen bonded to a carbon and the other a metal (a transition metal, lanthanide or
 * actinide); the bond then runs from the hydrogen. Any other bond is left alone.
 */
export function setBondAgostic(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const current = document.structure.bonds[edgeId];
  if (!edge || !current || current.type === 'agostic') return document;
  const { atoms, bonds, graph } = document.structure;
  const bondedToCarbon = (hydrogen: string) =>
    graph.edges.some((other) => {
      if (other.id === edgeId || (other.source !== hydrogen && other.target !== hydrogen)) return false;
      if (bonds[other.id]?.type === 'hydrogen' || bonds[other.id]?.type === 'agostic') return false;
      return atoms[other.source === hydrogen ? other.target : other.source]?.atomicNumber === 6;
    });
  const hydrogen = [edge.source, edge.target].find((id) => atoms[id]?.atomicNumber === 1 && bondedToCarbon(id));
  const metal = hydrogen === edge.source ? edge.target : edge.source;
  if (!hydrogen || !isTransitionMetal(atoms[metal]?.atomicNumber ?? 0)) return document;
  return reconcileValenceCharges(
    changeStructure(
      document,
      {
        graph: {
          ...graph,
          edges: graph.edges.map((candidate) =>
            candidate.id === edgeId ? { ...candidate, source: hydrogen, target: metal } : candidate
          )
        },
        bonds: { ...bonds, [edgeId]: { order: 0, type: 'agostic' } }
      },
      { clearAromaticity: true, clearStereoForEdge: edgeId }
    ),
    document
  );
}

/** The metals a C–H bond donates into: transition metals, lanthanides and actinides. */
export function isTransitionMetal(atomicNumber: number): boolean {
  const z = atomicNumber;
  return (z >= 21 && z <= 30) || (z >= 39 && z <= 48) || (z >= 57 && z <= 80) || (z >= 89 && z <= 112);
}

/**
 * ADR 0022 D5: a wavy single bond -- the configuration at `fromNodeId` (by default the end the bond
 * was drawn from, or its current wedge's narrow end) is deliberately undefined. The molfile marks
 * it "either", so RDKit's perception leaves that centre unspecified.
 */
export function setBondWavy(document: DrawingDocument, edgeId: string, fromNodeId?: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const bond = document.structure.bonds[edgeId];
  if (!edge || bond?.order !== 1 || bond.type !== 'covalent') return document;
  const current = bondStereoValues(document)[edgeId];
  const from = fromNodeId ?? (current?.type === 'wedge' || current?.type === 'wavy' ? current.fromNodeId : edge.source);
  if (![edge.source, edge.target].includes(from)) return document;
  if (current?.type === 'wavy' && current.fromNodeId === from) return document;
  const { [from]: _undefined, ...tetrahedral } = document.structure.stereochemistry.tetrahedral;
  const unset =
    from in document.structure.stereochemistry.tetrahedral
      ? changeStructure(document, { stereochemistry: { ...document.structure.stereochemistry, tetrahedral } })
      : document;
  return changeDepiction(unset, {
    bondStyles: { ...unset.depiction.bondStyles, [edgeId]: { style: 'wavy', fromAtomId: from } }
  });
}

export function reverseBondWavy(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const current = bondStereoValues(document)[edgeId];
  if (!edge || current?.type !== 'wavy') return document;
  return setBondWavy(document, edgeId, current.fromNodeId === edge.source ? edge.target : edge.source);
}

// B4 (#273): a dative (coordinate) bond -- the donor atom contributes both bonding electrons,
// drawn as an arrow donor -> acceptor. Always order 1. `donorNodeId` must be one of the edge's
// endpoints; passing null reverts the bond to plain covalent.
export function setBondDative(document: DrawingDocument, edgeId: string, donorNodeId: string | null): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const current = document.structure.bonds[edgeId];
  if (!edge || !current) return document;

  // Rule 4.4.1: a dative bond counts toward neither atom's auto charge (#1037), so toggling it
  // shifts both endpoints' bond sums -- reconcile N / O charges after either branch.
  if (donorNodeId === null) {
    if (current.type !== 'dative') return document;
    const { donorAtomId: _dropped, ...covalent } = current;
    return reconcileValenceCharges(
      changeStructure(
        document,
        { bonds: { ...document.structure.bonds, [edgeId]: { ...covalent, type: 'covalent', order: 1 } } },
        { clearAromaticity: true, clearStereoForEdge: edgeId }
      ),
      document
    );
  }

  if (donorNodeId !== edge.source && donorNodeId !== edge.target) return document;
  if (current.type === 'dative' && current.order === 1 && current.donorAtomId === donorNodeId) return document;
  return reconcileValenceCharges(
    changeStructure(
      document,
      { bonds: { ...document.structure.bonds, [edgeId]: { order: 1, type: 'dative', donorAtomId: donorNodeId } } },
      { clearAromaticity: true, clearStereoForEdge: edgeId }
    ),
    document
  );
}

export function reverseBondDative(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const current = document.structure.bonds[edgeId];
  if (!edge || current?.type !== 'dative') return document;
  return setBondDative(document, edgeId, current.donorAtomId === edge.source ? edge.target : edge.source);
}

export function setBondWedge(
  document: DrawingDocument,
  edgeId: string,
  style: 'solid' | 'hashed' | null,
  fromNodeId?: string
): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const bond = document.structure.bonds[edgeId];
  if (!edge || bond?.order !== 1) return document;
  const current = bondStereoValues(document)[edgeId];
  const resolvedFromNodeId = fromNodeId ?? (current?.type === 'wedge' ? current.fromNodeId : edge.source);
  if (![edge.source, edge.target].includes(resolvedFromNodeId)) return document;
  const nextBondStyles = { ...document.depiction.bondStyles };
  if (style === null) delete nextBondStyles[edgeId];
  else
    nextBondStyles[edgeId] = {
      style: style === 'solid' ? 'solid-wedge' : 'hashed-wedge',
      fromAtomId: resolvedFromNodeId
    };
  if (
    (style === null && !current) ||
    (current?.type === 'wedge' && current.style === style && current.fromNodeId === resolvedFromNodeId)
  ) {
    return document;
  }
  return changeDepiction(document, { bondStyles: nextBondStyles });
}

export function reverseBondWedge(document: DrawingDocument, edgeId: string): DrawingDocument {
  const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
  const current = bondStereoValues(document)[edgeId];
  if (!edge || current?.type !== 'wedge') return document;
  return setBondWedge(document, edgeId, current.style, current.fromNodeId === edge.source ? edge.target : edge.source);
}

// B3 (#271): mark an order-2 bond as "crossed" -- drawn with its two lines crossed, the IUPAC
// mark for a C=C whose cis/trans is deliberately undrawn. Stored as a `crossed-double` depiction
// bond style (sibling of the wedge styles). Turning it on also drops any explicit cis/trans
// configuration for that bond, since a crossed bond asserts the geometry is *not* specified.
export function setBondCrossedDouble(document: DrawingDocument, edgeId: string, crossed: boolean): DrawingDocument {
  const bond = document.structure.bonds[edgeId];
  if (!bond || bond.order !== 2) return document;

  const alreadyCrossed = bondStereoValues(document)[edgeId]?.type === 'crossed-double';
  const hasConfiguration = document.structure.stereochemistry.doubleBonds[edgeId] !== undefined;
  if (crossed === alreadyCrossed && !(crossed && hasConfiguration)) return document;

  const nextBondStyles = { ...document.depiction.bondStyles };
  if (crossed) nextBondStyles[edgeId] = { style: 'crossed-double' };
  else delete nextBondStyles[edgeId];

  if (crossed && hasConfiguration) {
    const { [edgeId]: _dropped, ...doubleBonds } = document.structure.stereochemistry.doubleBonds;
    return changeDepiction(
      changeStructure(document, {
        stereochemistry: { ...document.structure.stereochemistry, doubleBonds }
      }),
      { bondStyles: nextBondStyles }
    );
  }
  return changeDepiction(document, { bondStyles: nextBondStyles });
}

// #453: which of the two rings sharing a fusion double bond its second line faces, chosen by the
// user since either side is chemically valid. Stored the same way a wedge names its narrow end
// (an atom, not an abstract side) -- see fusedRingDoubleBondSides for why.
export function setBondDoubleSide(
  document: DrawingDocument,
  edgeId: string,
  facingAtomId: string | null
): DrawingDocument {
  if (document.structure.bonds[edgeId]?.order !== 2) return document;
  const current = bondStereoValues(document)[edgeId];
  const currentFacingAtomId = current?.type === 'double-bond-side' ? current.facingAtomId : null;
  if (currentFacingAtomId === facingAtomId) return document;
  const nextBondStyles = { ...document.depiction.bondStyles };
  if (facingAtomId === null) delete nextBondStyles[edgeId];
  else nextBondStyles[edgeId] = { style: 'double-bond-side', facingAtomId };
  return changeDepiction(document, { bondStyles: nextBondStyles });
}

// #453: the double-bond tool's click-to-retype (see applyArmedBondTool) already no-ops when a
// bond is already order 2 -- this is what runs instead for a fusion bond, cycling
// unset/default -> ring A -> ring B -> back to unset on each click.
export function cycleBondDoubleSide(document: DrawingDocument, edgeId: string): DrawingDocument {
  if (document.structure.bonds[edgeId]?.order !== 2) return document;
  const atoms = documentToAtoms(document);
  const lines = documentToLines(document);
  const bondOrders = drawnBondOrders(document);
  const sides = fusedRingDoubleBondSides(atoms, lines, bondOrders)[edgeId];
  if (!sides) return document; // not a fusion bond -- nothing to cycle
  const current = bondStereoValues(document)[edgeId];
  const currentFacingAtomId = current?.type === 'double-bond-side' ? current.facingAtomId : undefined;
  let next: string | null;
  if (currentFacingAtomId === sides[0].facingAtomId) next = sides[1].facingAtomId;
  else if (currentFacingAtomId === sides[1].facingAtomId) next = null;
  else next = sides[0].facingAtomId;
  return setBondDoubleSide(document, edgeId, next);
}

export function setAtomFormalCharge(document: DrawingDocument, nodeId: string, formalCharge: number): DrawingDocument {
  const current = document.structure.formalCharges[nodeId];
  if (current === undefined || current === formalCharge || !Number.isInteger(formalCharge)) {
    return document;
  }
  return changeStructure(
    document,
    {
      formalCharges: { ...document.structure.formalCharges, [nodeId]: formalCharge }
    },
    { clearAromaticity: true, clearStereoForNode: nodeId }
  );
}

/**
 * Overrides how an atom's charge is *displayed* (e.g. `-1/2` on a resonance-delocalized
 * carboxylate oxygen) without changing the underlying formal charge or any other chemistry -
 * see ADR 0003. Goes through `changeDepiction`, not `changeStructure`, and never clears
 * aromaticity/stereo perception, unlike `setAtomFormalCharge`.
 */
export function setAtomResonanceCharge(
  document: DrawingDocument,
  nodeId: string,
  resonanceCharge: number | null
): DrawingDocument {
  if (!document.structure.graph.nodes.some((node) => node.id === nodeId)) return document;
  if (resonanceCharge !== null && !Number.isFinite(resonanceCharge)) return document;
  const current = document.depiction.atomLabels[nodeId];
  if ((current?.resonanceCharge ?? null) === resonanceCharge) return document;
  const { resonanceCharge: _existingResonanceCharge, ...withoutResonanceCharge } = current ?? {
    hydrogenDisplay: 'auto' as const
  };
  return changeDepiction(document, {
    atomLabels: {
      ...document.depiction.atomLabels,
      [nodeId]: resonanceCharge === null ? withoutResonanceCharge : { ...withoutResonanceCharge, resonanceCharge }
    }
  });
}

export function setImplicitHydrogenDirection(
  document: DrawingDocument,
  nodeId: string,
  direction: Readonly<{ x: number; y: number }>
): DrawingDocument {
  if (!document.structure.graph.nodes.some((node) => node.id === nodeId)) return document;
  const length = Math.hypot(direction.x, direction.y);
  if (!Number.isFinite(length) || length === 0) return document;
  const normalized = {
    x: roundCoordinate(direction.x / length),
    y: roundCoordinate(direction.y / length)
  };
  const current = document.depiction.atomLabels[nodeId]?.hydrogenDirection;
  if (current?.x === normalized.x && current.y === normalized.y) return document;
  return changeDepiction(document, {
    atomLabels: {
      ...document.depiction.atomLabels,
      [nodeId]: { hydrogenDisplay: 'auto', ...document.depiction.atomLabels[nodeId], hydrogenDirection: normalized }
    }
  });
}
